import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";

export const Route = createFileRoute("/third-nail-wind-mitigation")({
  head: () => ({
    meta: [
      { title: "Third Nail Wind Mitigation in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Ask about third-nail and wind-mitigation improvements in Citrus County to strengthen eligible roof-to-wall connections." },
      { property: "og:title", content: "Third Nail Wind Mitigation in Citrus County, FL" },
      { property: "og:description", content: "Wind-mitigation improvements that may help you qualify for insurance credits." },
      { property: "og:url", content: canonical("/third-nail-wind-mitigation") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/third-nail-wind-mitigation") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Third Nail Wind Mitigation", href: "/third-nail-wind-mitigation" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Third Nail Wind Mitigation",
          serviceType: "Wind Mitigation",
          description: "Strengthening eligible roof-to-wall connections during a re-roof in Citrus County, FL.",
          url: canonical("/third-nail-wind-mitigation"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Third Nail Wind Mitigation", href: "/third-nail-wind-mitigation" }]} />
      <PageHero showRating
        eyebrow="Wind Mitigation"
        title="Third Nail Wind Mitigation in Citrus County, FL"
        subtitle="Strengthening eligible roof-to-wall connections during a re-roof. It may help you qualify for insurance credits — the insurance company determines the outcome."
      />
      <ServicePageBody title="Third Nail Wind Mitigation" service="Third Nail Wind Mitigation">
        <h2>What "Third Nail" Actually Means</h2>
        <p>
          Third-nail wind mitigation refers to strengthening certain roof-to-wall connections. The roof framing is commonly connected to the exterior walls with metal straps or clips. On some homes, the existing connector may not have enough fasteners to qualify for the strongest available connection category on a wind-mitigation inspection.
        </p>
        <p>
          When the connector and structure allow it, adding an approved third fastener may strengthen the connection between the roof framing and walls.
        </p>

        <h2>Careful Language on Insurance Savings</h2>
        <p>
          Wind-mitigation improvements <strong>may</strong> make a homeowner eligible for a discount or credit on the windstorm portion of an insurance premium. The actual amount depends on your inspection results, your insurance carrier and your policy. We do not guarantee a specific dollar savings, and neither should anyone else.
        </p>

        <h2>When It Makes Sense</h2>
        <ul>
          <li>You're already replacing your roof</li>
          <li>Your current wind-mitigation report shows a "toe nail" or weaker connection</li>
          <li>The existing connectors and structure allow the improvement</li>
          <li>You plan to stay in the home long enough for potential savings to matter</li>
        </ul>

        <h2>The Process</h2>
        <ul>
          <li>We assess the existing connections during your roof inspection</li>
          <li>We complete eligible improvements as part of your <Link to="/roof-replacement">roof replacement</Link></li>
          <li>A qualified wind-mitigation inspector documents the completed work</li>
          <li>You submit the report to your insurer</li>
        </ul>

        <p>
          Read more in our article on <Link to="/blog/attic-venting-soffits-gutters-roof-system">attic venting, soffits, gutters and wind mitigation</Link>, learn how <Link to="/florida-roof-insurance-rules">Florida&apos;s roof insurance rules work</Link>, or call {BUSINESS.phone} to talk it through.
        </p>
      </ServicePageBody>
    </>
  );
}
